import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { companyAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { ToggleLeft, Clock, DollarSign, Activity, FileText, Loader2 } from 'lucide-react';

const FeatureFlagsManagement = () => {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchCompany = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const response = await companyAPI.getOne(user.company_id);
      setCompany(response.data);
    } catch (err) {
      toast.error('Failed to load company');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const handleToggle = async (flag, value) => {
    setSaving(true);
    try {
      const newFlags = {
        ...company.feature_flags,
        [flag]: value
      };
      await companyAPI.updateFeatureFlags(user.company_id, newFlags);
      setCompany({
        ...company,
        feature_flags: newFlags
      });
      toast.success(`${flag.replace('_', ' ')} ${value ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update feature');
    } finally {
      setSaving(false);
    }
  };

  const features = [
    {
      key: 'attendance_enabled',
      name: 'Attendance Module',
      description: 'Clock in/out, shift management, attendance tracking',
      icon: Clock,
      color: 'text-emerald-500'
    },
    {
      key: 'payroll_enabled',
      name: 'Payroll Module',
      description: 'Salary computation, PH compliance, payslip generation',
      icon: DollarSign,
      color: 'text-blue-500'
    },
    {
      key: 'time_tracking_enabled',
      name: 'Time Tracking / Productivity',
      description: 'Activity monitoring, idle detection, productivity scoring',
      icon: Activity,
      color: 'text-purple-500'
    },
    {
      key: 'audit_enabled',
      name: 'Audit Trail',
      description: 'Immutable logging of all system events and changes',
      icon: FileText,
      color: 'text-gray-500'
    }
  ];

  if (loading) {
    return (
      <DashboardLayout title="Feature Flags">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Feature Flags" 
      subtitle="Enable or disable modules for your company"
    >
      <div className="max-w-2xl">
        <div className="space-y-4">
          {features.map((feature) => {
            const Icon = feature.icon;
            const isEnabled = company?.feature_flags?.[feature.key] ?? false;
            
            return (
              <Card key={feature.key} data-testid={`feature-${feature.key}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg bg-secondary ${feature.color}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{feature.name}</h3>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={(value) => handleToggle(feature.key, value)}
                      disabled={saving}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="mt-6 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Disabling a module will hide it from the UI but won&apos;t delete any existing data. 
              You can re-enable modules at any time.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default FeatureFlagsManagement;
